import { FC, KeyboardEvent } from "react";
import {
    Box,
    Button,
    Flex,
    Heading,
    Icon,
    Picture,
    Text,
} from "@smile2impress/components/relaunch";
import { InvisalignLogoLightIcon } from "@smile2impress/components";
import { useGoToBooking } from "hooks/useGoToBooking";

import Link from "components/Link";

import { BannerPaidSocialProps } from "./BannerPaidSocial.types";

const BADGE_BG_PALATINATE_BLUE = "#012df3";

const splitLabelLines = (label: string): string[] =>
    label.split(/\r?\n/).filter(Boolean);

export const BannerPaidSocial: FC<BannerPaidSocialProps> = ({
    content: {
        title,
        text,
        badge,
        cta,
        image,
        advantages,
        showInvisalignLogo = true,
    },
    headingAs = "h1",
}) => {
    const { goToBooking } = useGoToBooking();

    const {
        text: ctaLabel,
        onClick,
        link,
        iconLeft,
        iconRight,
        variant: ctaVariant,
    } = cta;

    const buttonVariant =
        typeof ctaVariant === "string"
            ? ctaVariant
            : Array.isArray(ctaVariant) && ctaVariant.length > 0
            ? ctaVariant[0]
            : "red";

    const handleCtaClick = () => {
        if (onClick) {
            onClick();
            return;
        }
        if (!link) goToBooking();
    };

    const handleCtaKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        handleCtaClick();
    };

    const ctaHref = link ?? undefined;

    return (
        <Flex
            as="section"
            direction="column"
            position="relative"
            width="100%"
            overflow="hidden"
            minH="596px"
            px={[2, 2, 4]}
            pt={[3, 3, 4]}
            pb={[3, 3, 4]}
            sx={{
                background:
                    "radial-gradient(ellipse 120% 100% at 50% 50%, rgb(241 42 46) 16.39%, rgb(176 21 23) 58.2%, rgb(144 11 12) 79.1%, rgb(111 0 0) 100%)",
            }}
        >
            <Box
                aria-hidden
                position="absolute"
                inset={0}
                overflow="hidden"
                pointerEvents="none"
                zIndex={0}
            >
                <Box
                    position="absolute"
                    display={["block", "block", "none"]}
                    left="50%"
                    bottom="0"
                    width="430px"
                    height="750px"
                    transform="translateX(-50%)"
                    bgColor="base.gunmetal"
                >
                    <Picture
                        defaultSrc={image.defaultSrc}
                        imageProps={{
                            objectPosition: "center center",
                            objectFit: "fill",
                        }}
                    />
                </Box>

                <Box
                    position="absolute"
                    inset={0}
                    display={["none", "none", "block"]}
                    bgColor="base.gunmetal"
                >
                    <Picture
                        {...image}
                        imageProps={{
                            objectFit: "cover",
                            objectPosition: "center center",
                            width: "100%",
                            height: "100%",
                        }}
                    />
                </Box>
            </Box>

            <Box
                aria-hidden
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                h={["240px", "280px", "400px"]}
                zIndex={0}
                sx={{
                    background:
                        "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.45) 64.159%)",
                }}
                pointerEvents="none"
            />

            <Flex
                direction="column"
                position="relative"
                zIndex={1}
                justifyContent="space-between"
                h="100%"
                flex={1}
            >
                <Flex justifyContent="flex-start" mb={3}>
                    {showInvisalignLogo ? (
                        <Icon
                            as={InvisalignLogoLightIcon}
                            w={["132px", "140px", "153px"]}
                            h={["35px", "37px", "40px"]}
                            aria-hidden
                        />
                    ) : null}
                </Flex>

                <Flex
                    flex={1}
                    direction="column"
                    justifyContent="flex-end"
                    minH={0}
                    w="100%"
                >
                    <Flex
                        direction={["column", "column", "row"]}
                        gap={[2, 2, "52px"]}
                        alignItems={["flex-start"]}
                        w="100%"
                    >
                        <Flex
                            direction="column"
                            alignItems="flex-start"
                            gap={["8px", "8px", 0.7]}
                            flex="1 1 0%"
                            minW={0}
                        >
                            {badge ? (
                                <Box
                                    alignSelf="flex-start"
                                    bg={BADGE_BG_PALATINATE_BLUE}
                                    px={"5px"}
                                    py="1px"
                                    borderRadius="4px"
                                >
                                    <Text
                                        as="span"
                                        fontFamily="heading"
                                        fontSize="11px"
                                        fontWeight="black"
                                        lineHeight="140%"
                                        color="base.white"
                                        textTransform="uppercase"
                                    >
                                        {badge}
                                    </Text>
                                </Box>
                            ) : null}

                            <Heading
                                as={headingAs}
                                textStyle={"H2"}
                                maxW="100%"
                                color="base.white"
                            >
                                {title}
                            </Heading>

                            {text ? (
                                <Text textStyle={["title3"]} color="base.white">
                                    {text}
                                </Text>
                            ) : null}
                        </Flex>

                        <Flex
                            direction="column"
                            gap={["8px", "8px", 0.7]}
                            flex="1 1 0%"
                            minW={0}
                            w="100%"
                        >
                            {advantages.map((row, index) => (
                                <Flex
                                    key={`${row.title}-${row.subtitle}-${index}`}
                                    gap={[5, 5, 4]}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    w="100%"
                                >
                                    <Text
                                        textStyle="H4"
                                        color="base.white"
                                        fontWeight="bold"
                                    >
                                        {splitLabelLines(row.title).map(
                                            (line, lineIndex) => (
                                                <span
                                                    key={`${line}-${lineIndex}`}
                                                >
                                                    {lineIndex > 0 ? (
                                                        <br />
                                                    ) : null}
                                                    {line}
                                                </span>
                                            )
                                        )}
                                    </Text>
                                    <Text
                                        textStyle="H2"
                                        color="base.white"
                                        whiteSpace="nowrap"
                                        flexShrink={0}
                                    >
                                        {row.subtitle}
                                    </Text>
                                </Flex>
                            ))}
                        </Flex>
                    </Flex>
                </Flex>

                <Button
                    as={ctaHref ? Link : undefined}
                    {...(ctaHref ? { href: ctaHref } : {})}
                    {...(!ctaHref
                        ? {
                              type: "button" as const,
                              onClick: handleCtaClick,
                              onKeyDown: handleCtaKeyDown,
                          }
                        : {})}
                    variant={buttonVariant}
                    aria-label={ctaLabel}
                    size="lg"
                    mt={[2, 2, 3]}
                    w="100%"
                    borderRadius="full"
                    fontFamily="body"
                    fontSize={14}
                    fontWeight="bold"
                    lineHeight="120%"
                    color="base.white"
                    justifyContent="center"
                >
                    <>
                        {iconLeft ? (
                            <Icon
                                w={1}
                                h={1}
                                as={iconLeft}
                                verticalAlign="baseline"
                                mr=".3em"
                                color="base.white"
                            />
                        ) : null}
                        {ctaLabel}
                        {iconRight ? (
                            <Icon
                                w={1}
                                h={1}
                                as={iconRight}
                                verticalAlign="baseline"
                                ml=".3em"
                                color="base.white"
                            />
                        ) : null}
                    </>
                </Button>
            </Flex>
        </Flex>
    );
};
